import type {
  DatasetChangeset,
  DatasetRowsPage,
  EditResult,
  WorkspaceLock,
} from '../types/dataGrid';

const ROUTE_PREFIX = '/chart-studio';

function getApiUrl(): string {
  if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    try {
      const stored = localStorage.getItem('vibe-dev-mode-config');
      if (stored) {
        const devConfig = JSON.parse(stored);
        if (devConfig.apiTarget === 'local') {
          return 'http://localhost:8000';
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:8000';
}

function getToken(): string | null {
  const key = Object.keys(sessionStorage).find((k) =>
    k.startsWith('oidc.user:')
  );
  if (!key) return null;
  try {
    const data = JSON.parse(sessionStorage.getItem(key) || '');
    return data?.access_token || null;
  } catch {
    return null;
  }
}

function buildHeaders(contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function getDatasetRows(
  datasetId: string,
  offset = 0,
  limit = 100
): Promise<DatasetRowsPage> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/datasets/${datasetId}/rows?offset=${offset}&limit=${limit}`;
  let response: Response;
  try {
    response = await fetch(url, {
      headers: buildHeaders('application/json'),
    });
  } catch (err) {
    throw new Error(`Cannot reach backend at ${getApiUrl()} — is it running?`);
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Failed to fetch rows (${response.status}): ${detail}`);
  }

  return response.json();
}

export async function saveDatasetEdits(
  datasetId: string,
  changeset: DatasetChangeset
): Promise<EditResult> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/datasets/${datasetId}/edit`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders('application/json'),
      body: JSON.stringify(changeset),
    });
  } catch (err) {
    throw new Error(`Cannot reach backend at ${getApiUrl()} — is it running?`);
  }

  if (response.status === 409) {
    const error = await response.json();
    throw new Error(error.message || 'Workspace is locked by another user');
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Failed to save edits (${response.status}): ${detail}`);
  }

  return response.json();
}

export async function acquireWorkspaceLock(
  workspaceId: string
): Promise<WorkspaceLock> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/workspaces/${workspaceId}/lock`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders('application/json'),
    });
  } catch (err) {
    throw new Error(`Cannot reach backend at ${getApiUrl()} — is it running?`);
  }

  if (response.status === 409) {
    const error = await response.json();
    throw new Error(error.message || 'Workspace is locked by another user');
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Failed to acquire lock (${response.status}): ${detail}`);
  }

  return response.json();
}

export async function releaseWorkspaceLock(
  workspaceId: string
): Promise<void> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/workspaces/${workspaceId}/lock`;
  await fetch(url, {
    method: 'DELETE',
    headers: buildHeaders('application/json'),
  });
}

export async function refreshWorkspaceLock(
  workspaceId: string
): Promise<WorkspaceLock> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/workspaces/${workspaceId}/lock`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: buildHeaders('application/json'),
  });

  if (!response.ok) {
    throw new Error('Failed to refresh workspace lock');
  }

  return response.json();
}
